import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// ════════════════════════════════════════════════════════════════════════════
// Unified-drawer FSM store — P3 of unified-drawer.md (#1433).
//
// One drawer surface, one FSM. Applies the workspace Frontend FSM pattern
// (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — the `phase` union + the exported TRANSITIONS table.
//   2. Origin-keyed store  — the origin is the APP-GLOBAL UI SINGLETON ('app'):
//                            a drawer is a single-instance surface, not per-session
//                            (unlike devPreviewStore, which is keyed per chat
//                            session). One identity ⇒ a flat store IS the
//                            origin-keyed store; no Map ceremony. persist middleware
//                            survives refresh — but a refresh NEVER restores an open
//                            drawer (phase demoted to 'closed' on rehydrate).
//                            EXCEPTION (#1566 Phase 1): the FILES drawer alone is
//                            re-opened after boot by uiStateStore.bootRestoreFileDrawer()
//                            — an explicit openDrawer('files') call from the shell's
//                            mount anchor effect, driven by the Class-B
//                            `va-ui-state-v1.fileDrawerOpen` bit (mirrored from THIS
//                            store's transitions). The demote here still runs first;
//                            this store's own persistence semantics are unchanged.
//   3. Server-authoritative — drawers are CLIENT UI, so there is no server truth for
//                            "is the drawer open". The *content* panels reconcile
//                            against their own server sources when they mount
//                            (Usage → GET /usage, Files → GET /files/tree, …); this
//                            store owns only which-panel-is-open. No frontend timeout
//                            guesses, no synthesized server claims (pattern rule 3).
//   4. Atomic transitions  — one user intent = one named action (openDrawer,
//                            closeDrawer), never scattered setters (R-A2).
//   5. Setpoint evals      — setpoint-evals/unified-drawer/ drives THIS store
//                            (simulator + SE-DR-* scenarios).
//
// The drawer "wire" is purely local user gesture — tap a BottomNav slot / the
// context pill (→ openDrawer), or swipe-down / scrim-tap / Back / Esc
// (→ closeDrawer). DrawerHost is the renderer (reads the hook selectors, writes
// through the named actions); this store only owns state, exactly as the pattern
// prescribes.
// ════════════════════════════════════════════════════════════════════════════

// ─── State types ───────────────────────────────────────────────────────────────

/**
 * Which content panel the single drawer surface is showing. Mirrors the §1
 * inventory in unified-drawer.md — the panels that all share the one shell.
 *
 *   sessions  the session list / commands / settings drawer family
 *   commands  slash-command palette
 *   settings  the Settings screen (today's SettingsModal content) as a sheet
 *   files     FileListDrawer (workspace tree / temp docs / plans / archive)
 *   usage     UsageTab (📊 Usage)
 *   tasks     live agent/task list (P4)
 *   ops       ops-panel embed via OpsProxyController (P4, admin-only)
 *   context   the ContextBar tuning sheet (effort / 1M / auto-save)
 *   runs      Agent Runs — live SDK/DTM workflow progress (admin-only)
 *
 * NOTE (agent-runs Phase 3): `runs` is a new panel, NOT a new trigger — it opens
 * through the existing `openDrawer` action, so it adds NO row to TRANSITIONS
 * (R-A1) and SE-DR-11's row-count assertion stays green. `tasks` (GitHub issues)
 * is a DIFFERENT panel — see the naming-collision note in the plan.
 */
export type DrawerPanel =
  | 'sessions'
  | 'commands'
  | 'settings'
  | 'files'
  | 'usage'
  | 'tasks'
  | 'ops'
  | 'context'
  | 'runs';

/**
 * FSM phase for the single drawer surface.
 *
 *   closed  no drawer shown.
 *   open    a drawer is shown; `panel` says which content is slotted.
 *
 * Full transition table is exported as `TRANSITIONS` below (R-A1).
 */
export type DrawerPhase = 'closed' | 'open';

export interface DrawerStoreShape {
  phase: DrawerPhase;
  /** The slotted content panel when phase === 'open'; null when closed. */
  panel: DrawerPanel | null;
  /**
   * Transient signal: the drawer was opened (or swapped) onto the sessions panel
   * specifically to surface the cascade tier-denial picker/hint. SessionListView
   * reads this REACTIVELY (a selector, not a window event) so the hint survives a
   * fresh mount — opening the drawer is what MOUNTS SessionListView, and a synchronous
   * window event would fire before its listener registers (the bug this flag fixes).
   * NEVER persisted (see partialize) — it is a one-shot UI cue cleared by
   * clearCascadeGate() once SessionListView has shown the hint.
   */
  cascadeGatePending: boolean;
}

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// The canonical FSM, mirrored from unified-drawer.md §2 (FSM). `run-all.sh`
// (SE-DR-11-fsm-coverage) validates that every distinct `trigger` here is covered
// by a simulator scenario's `// SE-COVERS:` marker — adding a transition MUST add a
// covering scenario. Each `trigger` is a NAMED store action — never an "anything
// else" path (R-A2). The open→open swap reuses the `openDrawer` trigger (tapping a
// different trigger while open swaps content without closing), so it adds no new
// coverage requirement beyond `openDrawer`.

export const TRANSITIONS: Array<{ from: DrawerPhase; to: DrawerPhase; trigger: string }> = [
  { from: 'closed', to: 'open', trigger: 'openDrawer' },
  { from: 'open', to: 'open', trigger: 'openDrawer' }, // swap content, no close
  { from: 'open', to: 'closed', trigger: 'closeDrawer' },
];

// ─── Persisted-storage backing ──────────────────────────────────────────────────
// A plain JSON storage suffices — the singleton state is two scalar fields, no Map
// to round-trip (cf. devPreviewStore's mapReplacer/mapReviver, which exist only
// because that store keys a Map by sessionId).

const jsonStorage = createJSONStorage(() => localStorage);

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useDrawerStore = create<DrawerStoreShape>()(
  persist(
    (): DrawerStoreShape => ({
      phase: 'closed',
      panel: null,
      cascadeGatePending: false,
    }),
    {
      name: 'va-drawer-v1',
      storage: jsonStorage,
      // Persist the last panel (so a future "reopen last" affordance can read it),
      // but DEMOTE the live phase to 'closed' on rehydrate: on reload there is no
      // user gesture keeping the drawer open, so we never restore an open drawer
      // (pattern rule: a refresh never resurrects transient UI). The next user tap
      // is authoritative.
      // cascadeGatePending is a one-shot UI cue, NEVER persisted (it would resurrect a
      // stale tier-denial hint on the next load with no live denial behind it).
      partialize: (state) => ({ phase: 'closed' as DrawerPhase, panel: state.panel }),
      // Authoritative demote lives in `merge` (runs DURING rehydration, its return
      // value BECOMES the state) — NOT in `onRehydrateStorage`. The old
      // onRehydrateStorage referenced `useDrawerStore.setState` while the `const
      // useDrawerStore` was still in its temporal dead zone (rehydration is
      // synchronous for localStorage, so it runs inside `create()` before the
      // binding is initialised); the ReferenceError was silently swallowed by
      // persist's `toThenable`, and the demote only "worked" by accident because
      // partialize already writes phase:'closed'. `merge` has no such TDZ hazard
      // (it receives both states as args) and makes the two invariants explicit and
      // load-bearing regardless of what the persisted blob happens to contain:
      //   - phase → 'closed': a reload never resurrects an open drawer (no live
      //     gesture holds it open; the next user tap is authoritative).
      //   - cascadeGatePending → false: a one-shot tier-denial cue is never
      //     restored (it is excluded from partialize, but pin it here too so a
      //     hand-edited / older blob carrying it can't leak a stale hint).
      merge: (persisted, current): DrawerStoreShape => ({
        ...current,
        ...(persisted as Partial<DrawerStoreShape>),
        phase: 'closed',
        cascadeGatePending: false,
      }),
    },
  ),
);

// ─── Actions (one named action per intent — R-A2) ───────────────────────────────

/**
 * closed → open(panel), or open(P) → open(panel) (swap content while open).
 * One user intent — "show this panel in the drawer" — as one atomic action.
 * Tapping a BottomNav slot or the context pill calls this.
 */
export function openDrawer(panel: DrawerPanel): void {
  // A plain open/swap is never a cascade-gate bounce — clear any stale pending cue so
  // a later sessions open doesn't re-show the hint.
  useDrawerStore.setState({ phase: 'open', panel, cascadeGatePending: false });
}

/**
 * Cascade tier-gate bounce: open (or swap to) the sessions panel AND flag that the open
 * was a tier-denial bounce, so SessionListView's reactive selector surfaces the picker +
 * hint on its (possibly fresh) mount. This is the SAME `openDrawer` FSM transition
 * (closed→open or open→open swap) — it adds NO new TRANSITIONS row (R-A1) — but as a
 * distinctly-named intent (R-A2) it also sets the transient cue the renderer reads.
 *
 * Reliability fix: previously the bounce was a synchronous `cascade-gated` window event;
 * when the drawer was closed or on another panel, SessionListView wasn't mounted yet, so
 * it registered its listener AFTER the event fired and missed the hint. Persistent store
 * state is read by a late-mounting selector — that is the fix.
 */
export function openForCascadeGate(): void {
  useDrawerStore.setState({ phase: 'open', panel: 'sessions', cascadeGatePending: true });
}

/**
 * SessionListView calls this once it has consumed the cascade-gate cue (shown the picker
 * + hint), so the cue doesn't re-fire on a later re-render / panel swap back.
 */
export function clearCascadeGate(): void {
  useDrawerStore.setState((s) =>
    s.cascadeGatePending ? { cascadeGatePending: false } : s,
  );
}

/**
 * open → closed. Swipe-down handle / scrim tap / Back gesture / Esc.
 * Clears the slotted panel + any pending cascade-gate cue. No-op while already closed.
 */
export function closeDrawer(): void {
  useDrawerStore.setState((s) =>
    s.phase === 'closed'
      ? (s.cascadeGatePending ? { cascadeGatePending: false } : s)
      : { phase: 'closed', panel: null, cascadeGatePending: false },
  );
}

// ─── Non-hook accessors (for effect bodies / event handlers) ────────────────────

export function getDrawerPhase(state: DrawerStoreShape): DrawerPhase {
  return state.phase;
}

export function getDrawerPanel(state: DrawerStoreShape): DrawerPanel | null {
  return state.phase === 'open' ? state.panel : null;
}

// ─── Hook selectors — the ONLY supported way to read state in React ─────────────

/** Subscribe to the drawer FSM phase. */
export function useDrawerPhase(): DrawerPhase {
  return useDrawerStore((s) => s.phase);
}

/** Subscribe to the currently-open panel (null when closed). */
export function useDrawerPanel(): DrawerPanel | null {
  return useDrawerStore((s) => (s.phase === 'open' ? s.panel : null));
}

/** Subscribe to the full drawer entry ({ phase, panel, cascadeGatePending }). */
export function useDrawerEntry(): DrawerStoreShape {
  return useDrawerStore(
    useShallow((s) => ({ phase: s.phase, panel: s.panel, cascadeGatePending: s.cascadeGatePending })),
  );
}

/**
 * Subscribe to the transient cascade tier-gate cue. True only while the drawer was
 * bounced to the sessions panel by a tier denial and SessionListView has not yet
 * consumed it (then it calls clearCascadeGate()). Drives the picker + hint reliably even
 * when the drawer was already open on another panel (the listener-race fix).
 */
export function useCascadeGatePending(): boolean {
  return useDrawerStore((s) => s.cascadeGatePending);
}
